// src/components/impact/ImpactInitiatives.tsx
import React from 'react';
import { motion } from 'framer-motion';
import {Button} from '@dopomogai/ui';
import { Badge } from '@dopomogai/ui';
import { renderIcon } from '@dopomogai/utils';
import clsx from 'clsx'; // Import clsx

interface Action {
  type: string;
  payload: any;
}

interface Project {
  id: string;
  icon: string;
  title: string;
  text: string;
  status: string;
  cta: {
    text: string;
    action: Action;
  };
}

interface InitiativesData {
  headline: string;
  intro: string;
  projects: Project[];
}

interface ImpactInitiativesProps {
  initiativesData: InitiativesData;
  onAction: (actionType: string, payload?: any) => void;
}

const InitiativeCard: React.FC<{ project: Project; index: number; onAction: ImpactInitiativesProps['onAction'] }> = ({ project, index, onAction }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: index * 0.1, ease: 'easeOut' } },
  };

  const getStatusColor = (status: string): 'green' | 'blue' | 'yellow' | 'slate' => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes('live')) return 'green';
    if (lowerStatus.includes('development')) return 'blue';
    if (lowerStatus.includes('conceptual')) return 'yellow';
    return 'slate';
  };

  return (
    // --- THE FIX: Added 'relative group' and animator props to the outer div ---
    <motion.div
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="relative group h-full"
    >
      {/* This div is the shadow that appears on hover */}
      <div className="absolute inset-0 bg-black rounded-xl transition-transform duration-200 ease-out group-hover:translate-x-1 group-hover:translate-y-1" />

      {/* This div is the main card content that moves on hover */}
      <div className="relative bg-white rounded-xl border-2 border-black p-6 md:p-8 flex flex-col h-full transition-transform duration-200 ease-out group-hover:-translate-x-1 group-hover:-translate-y-1">
        <div className="flex items-start justify-between mb-4">
          <div className="p-3 bg-slate-100 border-2 border-black rounded-lg w-fit">
            {renderIcon(project.icon, { size: 28, className: 'text-gray-800' })}
          </div>
          <Badge text={project.status} accentColor={getStatusColor(project.status)} />
        </div>
        <h3 className="text-xl font-bold text-gray-900 leading-tight">{project.title}</h3>
        <p className="mt-2 text-slate-600 flex-grow leading-relaxed">{project.text}</p>
        {project.cta && (
          <div className="mt-6 pt-6 border-t-2 border-slate-200">
            <Button
              text={project.cta.text}
              onClick={() => onAction(project.cta.action.type, project.cta.action.payload)}
              variant="secondary"
              fullWidth
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export const ImpactInitiatives: React.FC<ImpactInitiativesProps> = ({ initiativesData, onAction }) => {
  return (
    <section className="bg-slate-100 py-16 md:py-24">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{initiativesData.headline}</h2>
          <p className="mt-4 text-lg text-slate-600">{initiativesData.intro}</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
          {initiativesData.projects.map((project, index) => (
            <InitiativeCard key={project.id} project={project} index={index} onAction={onAction} />
          ))}
        </div>
      </div>
    </section>
  );
};

